<template>
  <a-modal
    v-model:visible="visible"
    :title="title"
    :width="420"
    :after-close="afterClose"
    :footer="null"
  >
    <a-form
      ref="form"
      :label-col="{ span: 7 }"
      :model="formState"
      :rules="formRules"
      :label-align="'left'"
      :footer="null"
    >
      <a-form-item label="名称" name="name">
        <a-input v-model:value="formState.name" />
      </a-form-item>
      <a-form-item label="类别" name="type">
        <a-select
          ref="select"
          v-model:value="formState.type"
          :bordered="false"
          :options="options1"
          class="pt-[2px]"
        ></a-select>
      </a-form-item>
      <a-form-item label="设备" name="deviceType">
        <a-select
          ref="select"
          v-model:value="formState.deviceType"
          :bordered="false"
          class="pt-[2px]"
        >
          <a-select-option
            v-for="option in wholeDevices"
            :key="option.deviceType"
          >
            <template
              v-if="
                GlobalConfig.showDeviceTypeProductionName && option.productName
              "
            >
              {{ option.productName }}
            </template>
            <template v-else>
              {{ option.deviceType }}
              <span style="color: rgb(163, 163, 163)" class="ml-1">
                {{ option.size }}
              </span>
            </template>
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="预览图" name="previewImage">
        <image-picker
          class="rounded-[8px] my-2"
          :style="{
            border: previewImage ? '2px solid #333333' : '1px solid red'
          }"
          :size="150"
          :img="previewImage"
          :disabled="!!attrDisabled"
          :allow-preview="false"
          @change="handleChange"
          @delete="handleDelete"
        />
        <div v-if="!previewImage" class="text-red-500">请上传预览图</div>
      </a-form-item>
      <a-form-item class="!pt-4" :wrapper-col="{ offset: 6 }">
        <a-button key="back" class="" @click="() => (visible = false)">
          {{ cancelText }}
        </a-button>
        <a-button key="submit" type="primary" class="ml-2.5" @click="handleOk">
          {{ okText }}
        </a-button>
      </a-form-item>
    </a-form>
  </a-modal>
  <Teleport to="#app">
    <div class="absolute top-24 right-1/2 transform translate-x-[310px]">
      <a-popover
        v-model:visible="popoverVisble"
        placement="bottom"
        overlay-class-name="myPopClass"
      >
        <template #content>已添加到模板库</template>
      </a-popover>
    </div>
  </Teleport>
</template>
<script setup lang="ts">
import { computed, nextTick, reactive, ref } from 'vue';
import { useEditorStore } from '@/store/use-editor-store';
import type { SelectProps } from 'ant-design-vue';
import { useWatchfaceDevices, useWatchfaceScreenShot } from '@/hooks';
import {
  TemplateService,
  WatchfaceTemplateItem,
  WatchfaceTemplateType
} from '@/services/template.service';
import { Modal } from 'ant-design-vue';
import { getImageBase64 } from '@/utils/fs/image.utils';
import { Shape } from '@/services/shape';
import { FormExpose } from 'ant-design-vue/lib/form/Form';
import { cloneDeep } from 'lodash-es';
import { GlobalConfig } from '@/services/gloabl.config';
import { useWatchfaceStore } from '@/store/use-watchface-store';

defineProps({
  title: {
    type: String,
    default: '创建为模板'
  },
  okText: {
    type: String,
    default: '创建'
  },
  cancelText: {
    type: String,
    default: '取消'
  }
});
const emit = defineEmits<{ (event: 'update'): void }>();

const watchfaceStore = useWatchfaceStore();

const { screenshot } = useWatchfaceScreenShot();
const editorStore = useEditorStore();
const attrDisabled = computed(() => editorStore.attrDisabled);
const visible = ref<boolean>(false);
const previewImage = ref('');
interface MyForm extends HTMLFormElement, FormExpose {}
const form = ref<MyForm | null>(null);
const popoverVisble = ref(false);
const formState: {
  name: string;
  type: string;
  deviceType: string;
  size: string;
} = reactive({
  name: '',
  type: '时间日期',
  deviceType: watchfaceStore.deviceType,
  size: '464x464'
});
const formRules = {
  name: [
    {
      required: true,
      message: '请输入内容'
    }
  ],
  type: [
    {
      required: true,
      message: '请输入内容'
    }
  ],
  deviceType: [
    {
      required: true,
      message: '请输入内容'
    }
  ]
};
//创建框
const ids = ref<string[]>([]);
let isCreateTemplate = false;
const showModal = (shapeIds: string[]) => {
  isCreateTemplate = true;
  ids.value = cloneDeep(shapeIds);
  visible.value = true;
  formState.name = Shape.getShape(shapeIds[0])?.attrs.name || '';
  formState.deviceType = watchfaceStore.deviceType;
  screenshot(undefined, { shapeIds }).then((imgBase64) => {
    if (imgBase64) previewImage.value = imgBase64 as string;
  });
};

//编辑框
let editId = '';
let editData = {} as WatchfaceTemplateItem;
const showEditModal = (data: WatchfaceTemplateItem) => {
  editId = data.id;
  editData = data;
  visible.value = true;
  formState.name = data.name || '';
  formState.type = data.type || '';
  formState.deviceType = data.deviceType || '';
  previewImage.value = data.preview || '';
};
defineExpose({ showModal, showEditModal });

//处理类别
const typeArr: {
  value: string;
  label: string;
}[] = reactive([]);
for (let i = 0; i < TemplateService.types.length; i++) {
  typeArr.push({
    value: TemplateService.types[i],
    label: TemplateService.types[i]
  });
}
const options1 = ref<SelectProps['options']>(typeArr);
const { wholeDevices } = useWatchfaceDevices();

//预览图相关事件
const handleChange = async (imagePath: string) => {
  previewImage.value = (await getImageBase64(imagePath)) || '';
};
const handleDelete = () => {
  previewImage.value = '';
};

//表单验证
const dataValid = async () => {
  let valid = false;
  try {
    const names = Object.keys(formRules);
    await form.value?.validate(names);
    valid = true;
  } catch (error) {
    valid = false;
  }
  return valid;
};

//创建或编辑模板
const handleOk = async () => {
  let valid = await dataValid();
  if (!valid || !previewImage.value) return;
  await nextTick();
  const template = new TemplateService();
  let exist = await template.testExistsName(formState.name);
  const _createTemplate = () =>
    (async () => {
      await template.createTemplate(
        {
          name: formState.name,
          type: formState.type as WatchfaceTemplateType,
          deviceType: formState.deviceType,
          preview: previewImage.value
        },
        {
          shapeIds: ids.value,
          colorId: editorStore.themeColorId
        },
        true
      );
      visible.value = false;
      popoverVisble.value = true;
      setTimeout(() => {
        popoverVisble.value = false;
      }, 3000);
    })();
  const _updateTemplate = async () => {
    await template.updateTemplate(editId, {
      name: formState.name,
      type: formState.type as WatchfaceTemplateType,
      deviceType: formState.deviceType,
      preview: previewImage.value,
      createdAt: editData.createdAt,
      updatedAt: editData.updatedAt
    });
    emit('update');
    visible.value = false;
  };
  //重名
  if (exist) {
    Modal.confirm({
      title: '提示',
      content: '模板名称已存在，是否覆盖已有模板？',
      okText: '覆盖',
      cancelText: '取消',
      onOk: isCreateTemplate ? _createTemplate : _updateTemplate
    });
  } else isCreateTemplate ? _createTemplate() : _updateTemplate();
};

//移除表单项的校验结果
const afterClose = () => {
  form.value?.clearValidate();
};
</script>
<style lang="less">
.myPopClass {
  .ant-popover-inner {
    background-color: #000000;
  }
  .ant-popover-inner-content {
    color: #c1c1c1;
  }
  .ant-popover-arrow-content {
    background-color: #000000 !important;
  }
}
.modal-create {
  .ant-modal-content {
    background-color: #000000;
    border-radius: 20px;
    .ant-modal-header {
      border-radius: 20px 20px 0 0;
      background-color: #000000;
    }
  }
}
</style>
<style scoped lang="less">
:deep(
    .ant-form-item-label
      > label.ant-form-item-required:not(
        .ant-form-item-required-mark-optional
      )::before
  ) {
  content: '';
}

:deep(.ant-form-item-label > label::after) {
  content: '';
}
</style>
